<script lang="ts">
  import { LoaderCircle, CircleAlert } from '@lucide/svelte'
  import useUploadedFilesQueries from '../hooks/useUploadedFilesQueries'
  import { useProjectQueries } from '../../../hooks/project'
  import i18n from '../../../i18n'
  import { fileLoadingState, resetFileLoadingState } from '../../../stores/file-loading'

  const { loading }: { loading: boolean } = $props()

  const { useFileUploadIsMutating } = useUploadedFilesQueries()
  const { useProjectSaveIsMutating } = useProjectQueries()
  const fileUploadIsMutating = useFileUploadIsMutating()
  const projectSaveIsMutating = useProjectSaveIsMutating()

  const getLoadingStatusText = (status: string): string => {
    switch (status) {
      case 'reading':
        return $i18n.t('project.loadingState.readingFile')
      case 'parsing':
        return $i18n.t('project.loadingState.parsingFile')
      case 'processing':
        return $i18n.t('project.loadingState.processingFile')
      case 'finalizing':
        return $i18n.t('project.loadingState.finalizingFile')
      default:
        return $i18n.t('project.loadingState.importingFile')
    }
  }

  const getErrorText = (errorMessage: string): string => {
    switch (errorMessage) {
      case 'fileTooLarge':
        return $i18n.t('project.loadingState.fileTooLarge')
      default:
        return $i18n.t('project.loadingState.uploadError')
    }
  }

  const dismissError = () => {
    resetFileLoadingState()
  }
</script>

<div class="loading-container">
  {#if $fileLoadingState.status === 'error'}
    <button class="loading-state error" onclick={dismissError}>
      <div class="loading-icon error-icon">
        <CircleAlert color="var(--error)" size="1.25rem" />
      </div>
      <div class="loading-content">
        <div class="loading-text error-text">
          {getErrorText($fileLoadingState.errorMessage ?? '')}
        </div>
        {#if $fileLoadingState.fileName}
          <div class="loading-filename">{$fileLoadingState.fileName}</div>
        {/if}
      </div>
    </button>
  {:else if fileUploadIsMutating.current > 0}
    <div class="loading-state">
      <div class="loading-icon">
        <LoaderCircle color="var(--primary)" size="1.25rem" />
      </div>
      <div class="loading-content">
        <div class="loading-text">
          {#if $fileLoadingState.status !== 'idle'}
            {getLoadingStatusText($fileLoadingState.status)}
          {:else}
            {$i18n.t('project.loadingState.importingFile')}
          {/if}
        </div>
        {#if $fileLoadingState.fileName}
          <div class="loading-filename">{$fileLoadingState.fileName}</div>
        {/if}
        {#if $fileLoadingState.progress > 0}
          <div class="loading-progress-bar">
            <div class="loading-progress-fill" style="width: {$fileLoadingState.progress}%"></div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
  {#if loading}
    <div class="loading-state">
      <div class="loading-icon">
        <LoaderCircle color="var(--primary)" size="1.25rem" />
      </div>
      <div class="loading-text">
        {$i18n.t('project.loadingState.loadingProject')}
      </div>
    </div>
  {/if}
  {#if projectSaveIsMutating.current > 0}
    <div class="loading-state">
      <div class="loading-icon">
        <LoaderCircle color="var(--primary)" size="1.25rem" />
      </div>
      <div class="loading-text">
        {$i18n.t('project.loadingState.savingProject')}
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .loading-container {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    position: absolute;
    right: 1rem;
    top: 1rem;

    .loading-state {
      align-items: flex-start;
      background: var(--background);
      border-radius: 0.5rem;
      border: 1px solid var(--grid-stroke);
      display: flex;
      flex-direction: row;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      min-width: 200px;

      &.error {
        border-color: var(--error);
        cursor: pointer;

        &:hover {
          background: var(--background-hover);
        }

        .error-icon {
          animation: none;
        }

        .error-text {
          color: var(--error);
        }
      }

      .loading-icon {
        display: flex;
        margin-top: 2px;
        animation: spin 1s linear infinite;
      }

      .loading-content {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        flex: 1;

        .loading-filename {
          font-size: 0.75rem;
          color: var(--text-secondary);
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .loading-progress-bar {
          height: 4px;
          background: var(--grid-stroke);
          border-radius: 2px;
          overflow: hidden;
          margin-top: 0.25rem;

          .loading-progress-fill {
            height: 100%;
            background: var(--primary);
            border-radius: 2px;
            transition: width 0.2s ease-out;
          }
        }
      }
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
